// components/MyPage/Timeline.jsx
// 소식함 – MUI TimeLine + 가로 여백(gutter) 파라미터 지원

// src/components/MyPage/Timeline.jsx
// 소식함 – MUI Timeline + 가로 여백(gutter) 지원

import { useEffect, useState } from 'react';
import {
  Typography, Button, Box, useMediaQuery, Stack, Pagination,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../../contexts/UserContext';

import {
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
  TimelineContent, TimelineDot, TimelineOppositeContent,
} from '@mui/lab';

dayjs.extend(relativeTime);

const PAGE_SIZE = 10;




const dotColorFor = (t, isRead) => {
  // 읽은 항목은 variant로 처리하므로 color는 아무거나 기본값 사용
  if (isRead) return 'primary';
  switch (t) {
    case 'guestbook_post': return 'success';
    case 'comment': return 'primary';
    case 'reply': return 'secondary';
    case 'like': return 'warning';
    default: return 'primary';
  }
};

export default function TimelinePage({ gutter = 0 }) {
  const [logs, setLogs] = useState([]);
  const [filterUnread, setFilter] = useState(false);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userInfo } = useUser(); // ✅ 여기서 userInfo 획득

  // 컴포넌트 안에 (userInfo 사용 가능 위치)
  const getTargetUrl = (log) => {
    console.log("getTargetUrl called with log:", log);

    // 1) 게스트북 계열은 무조건 앵커/탭 우선 (target_url보다 먼저!)
    // 1) 게스트북 계열 우선
    if (log.type === 'guestbook_post' || log.type === 'guestbook_reply') {
      const owner =
        log.owner_username ||
        (() => {
          try {
            const u = new URL(log.target_url || '', window.location.origin);
            const m = u.pathname.match(/^\/u\/([^\/?#]+)\/home/); // <-- 여기!
            return m ? m[1] : null;
          } catch { return null; }
        })() ||
        userInfo?.username;

      if (owner) {
        if (log.guestbook_id) return `/u/${owner}/home#guestbook-${log.guestbook_id}`;
        return `/u/${owner}/home?tab=guestbook`; // 항목 지정 없으면 탭으로
      }
    }

    // 2) 그 외 타입은 target_url 우선
    if (log.target_url) {
      try {
        const u = new URL(log.target_url, window.location.origin);
        if (u.origin === window.location.origin) {
          return `${u.pathname}${u.search}${u.hash}`;
        }
        return u.toString();
      } catch {
        return log.target_url.startsWith('/') ? log.target_url : `/${log.target_url}`;
      }
    }

    // 3) target_url 없는 fallback
    switch (log.type) {
      case 'comment':
      case 'reply': {
        const id = log.post_id ?? log.target_post_id ?? '';
        return id ? `/posts/${id}` : '/main/mypage';
      }
      case 'like': {
        const id = log.post_id ?? '';
        return id ? `/posts/${id}` : '/main/mypage';
      }
      case 'guestbook_post':
      case 'guestbook_reply': {
        const owner = log.owner_username || userInfo?.username;
        const anchor = log.guestbook_id ? `#guestbook-${log.guestbook_id}` : '';
        return owner ? `/u/${owner}/home${anchor || '?tab=guestbook'}` : '/main/mypage';
      }
      default:
        return '/main/mypage';
    }
  };


  /* ───────── 알림 로드 ───────── */
  useEffect(() => {
    api.get('/api/notifications/logs/')
      .then(res => setLogs(res.data.logs || []))
      .catch(() => alert('소식함을 불러오지 못했습니다.'));
  }, []);

  /* ───────── 핸들러 ───────── */
  const handleMarkAllRead = () =>
    api.post('/api/notifications/logs/mark-all-read/')
      .then(() => setLogs(prev => prev.map(l => ({ ...l, is_read: true }))))
      .catch(() => alert('전체 읽음 처리 실패'));

  const handleDeleteAll = () => {
    if (!window.confirm('정말 모든 알림을 삭제하시겠습니까?')) return;
    api.post('/api/notifications/logs/delete-all/')
      .then(() => setLogs([]))
      .catch(() => alert('전체 삭제 실패'));
  };

  /* ───────── 익명 메시지 치환 ───────── */
  const getDisplayMessage = (log) =>
    log.is_anonymous
      ? log.message.replace(/([^\s]+)님/, '익명님') // 모든 사용자에 대해 치환
      : log.message;

  /* ───────── 필터링 & 페이징 ───────── */
  const filtered = filterUnread ? logs.filter(l => !l.is_read) : logs;
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const slice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ───────── 렌더 ───────── */
  return (
    <Box sx={{ px: gutter }}>
      {/* 상단 컨트롤 */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between',
          mb: 2, flexWrap: 'wrap', gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          📬 소식함
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={filterUnread ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setFilter(!filterUnread)}
          >
            {filterUnread ? '전체 보기' : '미읽음만 보기'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleMarkAllRead}>
            모두 읽음 처리
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleDeleteAll}
          >
            모두 삭제 처리
          </Button>
        </Box>
      </Box>

      {/* 본문 */}
      {filtered.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>
          새로운 소식이 없습니다.
        </Typography>
      ) : isMobile ? (
        /* ── 모바일: 아코디언 ── */
        <Stack spacing={2}>
          {slice.map(log => (
            <Accordion key={log.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArticleIcon fontSize="small" />
                  <Typography fontWeight={log.is_read ? 400 : 600}>
                    {dayjs(log.created_at).format('YYYY.MM.DD')} 알림
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                onClick={async () => {
                  try { await api.post(`/api/notifications/logs/${log.id}/read/`); } catch { }
                  setLogs(prev => prev.map(l => l.id === log.id ? { ...l, is_read: true } : l));
                  const dest = getTargetUrl(log);
                  if (/^https?:\/\//i.test(dest)) {
                    window.location.assign(dest);
                  } else {
                    navigate(dest);
                  }
                }}
                sx={{
                  backgroundColor: log.is_read ? '#f5f5f5' : '#fffde7',
                  borderLeft: `4px solid ${log.is_read ? '#ccc' : theme.palette.primary.main}`,
                  px: 2, py: 1, cursor: 'pointer',
                }}
              >
                <Typography fontSize={14} fontWeight={log.is_read ? 400 : 600}>
                  {getDisplayMessage(log)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {dayjs(log.created_at).fromNow()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : (
        /* ── 데스크톱: 타임라인 ── */
        <Timeline position="alternate">
          {slice.map((log, idx) => (
            <TimelineItem key={log.id} position={log.type === 'like' ? 'left' : 'right'}>
              <TimelineOppositeContent sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {dayjs(log.created_at).format('YYYY-MM-DD HH:mm')}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot
                  variant={log.is_read ? 'outlined' : 'filled'}
                  color={dotColorFor(log.type, log.is_read)} // 아래 dotColorFor 수정 반영
                />
                {idx !== slice.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                <Box
                  onClick={async () => {
                    try { await api.post(`/api/notifications/logs/${log.id}/read/`); } catch { }
                    // 로컬 상태 즉시 반영
                    setLogs(prev => prev.map(l => l.id === log.id ? { ...l, is_read: true } : l));
                    const dest = getTargetUrl(log);
                    if (/^https?:\/\//i.test(dest)) {
                      window.location.assign(dest);
                    } else {
                      navigate(dest);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: log.is_read ? 400 : 600,
                    color: log.is_read ? 'text.primary' : 'primary.main',
                    mb: 1,
                  }}
                >
                  {getDisplayMessage(log)}
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}

      {/* 페이지네이션 */}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
